import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

function Breadcrumb({ items }: { items: { label: string; href?: string }[] }) {
  return (
    <nav aria-label="面包屑导航" className="w-full px-4 md:px-6 py-3">
      <ol className="flex items-center flex-wrap gap-1 text-xs text-foreground-500">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-1">
            {i > 0 && (
              <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-arrow-right-s-line"></i>
              </span>
            )}
            {item.href ? (
              <Link to={item.href} className="hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap">
                {item.label}
              </Link>
            ) : (
              <span className="text-foreground-700">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function BrandIntro() {
  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14" aria-label="品牌介绍">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">关于天狼影院</h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
            天狼影院是国内领先的在线影视平台，致力于为广大用户提供高品质、免费、流畅的追剧观影体验。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">我们的故事</h3>
            <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              <p>
                天狼影院成立于2020年，由一群热爱影视的年轻人创立。我们深知追剧爱好者在寻找高质量影视资源时的困扰——卡顿、缓冲、画质模糊、广告繁多。因此，我们立志打造一个真正为用户着想的在线观影平台。
              </p>
              <p>
                经过多年的技术积累和内容建设，天狼影院已经发展成为涵盖电视剧、电影、动漫、综艺、纪录片等多品类的综合性影视平台，日活跃用户超过百万，累计服务用户超过千万人次。
              </p>
              <p>
                我们始终坚持"用户至上"的理念，采用先进的CDN加速技术和智能转码系统，确保每一位用户都能享受到流畅不缓冲、画质清晰的观影体验。
              </p>
            </div>
          </div>
          <div className="rounded-xl overflow-hidden bg-background-200 h-72 md:h-80">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20home%20theater%20setup%20with%20large%20screen%20showing%20cinematic%20content%2C%20cozy%20living%20room%20with%20warm%20ambient%20lighting%2C%20comfortable%20sofa%2C%20premium%20entertainment%20experience%2C%20dark%20moody%20atmosphere&width=800&height=600&seq=about-cinema-01&orientation=landscape"
              alt="天狼影院品牌形象"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function MissionValues() {
  const values = [
    {
      icon: 'ri-star-line',
      title: '品质优先',
      desc: '所有内容经过严格筛选，确保高清画质和流畅播放体验，让每一帧画面都清晰动人。',
    },
    {
      icon: 'ri-shield-check-line',
      title: '免费观影',
      desc: '坚持免费模式，无需注册即可观看，让优质影视内容触手可及，真正实现全民共享。',
    },
    {
      icon: 'ri-flashlight-line',
      title: '技术驱动',
      desc: '采用先进的视频编码和CDN分发技术，智能适配不同网络环境，告别卡顿和缓冲。',
    },
    {
      icon: 'ri-user-heart-line',
      title: '用户至上',
      desc: '以用户体验为核心，持续优化产品功能，倾听用户反馈，做最懂追剧人的影视平台。',
    },
  ];

  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-label="核心价值">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">我们的理念</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
          {values.map((v) => (
            <div key={v.title} className="flex gap-4 p-5 rounded-xl bg-background-50 border border-background-200/60">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                <i className={`${v.icon} text-lg`}></i>
              </span>
              <div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-1">{v.title}</h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const reviews = [
    {
      name: '小王',
      role: '资深追剧达人',
      content: '用了天狼影院快两年了，资源真的超全！追什么剧都有，而且高清不卡，是我用过最好的免费影视平台。',
      rating: 5,
    },
    {
      name: '李姐',
      role: '全职妈妈',
      content: '带孩子间隙就想追追剧，天狼影院不用注册直接看，太方便了。画质也很清晰，推荐给身边的妈妈们了。',
      rating: 5,
    },
    {
      name: '小陈',
      role: '大学生',
      content: '宿舍网速一般，但天狼影院加载很快，基本不缓冲。动漫资源也很丰富，追番神器！',
      rating: 4,
    },
    {
      name: '张哥',
      role: 'IT从业者',
      content: '作为技术人很欣赏天狼影院的播放体验，CDN做得不错。内容更新也快，热门剧同步更新很及时。',
      rating: 5,
    },
  ];

  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14" aria-label="用户口碑">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">用户口碑</h2>
          <p className="text-sm text-foreground-500">听听追剧达人们怎么说</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
          {reviews.map((r, i) => (
            <div key={i} className="p-5 rounded-xl bg-background-100 border border-background-200/60">
              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: 5 }, (_, j) => (
                  <span key={j} className={`w-4 h-4 flex items-center justify-center ${j < r.rating ? 'text-primary-500' : 'text-background-300'}`}>
                    <i className="ri-star-fill text-sm"></i>
                  </span>
                ))}
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                &ldquo;{r.content}&rdquo;
              </p>
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 text-xs font-semibold">
                  {r.name[0]}
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-800">{r.name}</p>
                  <p className="text-xs text-foreground-500">{r.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactInfo() {
  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-label="联系我们">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">联系我们</h2>
          <p className="text-sm text-foreground-500">如有任何问题或建议，欢迎通过以下方式联系我们</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
          <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/60">
            <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-3">
              <i className="ri-mail-line text-xl"></i>
            </span>
            <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h3>
            <p className="text-xs text-foreground-500">contact@tianlangcinema.com</p>
          </div>
          <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/60">
            <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-3">
              <i className="ri-wechat-line text-xl"></i>
            </span>
            <h3 className="text-sm font-semibold text-foreground-800 mb-1">微信公众号</h3>
            <p className="text-xs text-foreground-500">搜索 &ldquo;天狼影院&rdquo;</p>
          </div>
          <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/60">
            <span className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mx-auto mb-3">
              <i className="ri-weibo-line text-xl"></i>
            </span>
            <h3 className="text-sm font-semibold text-foreground-800 mb-1">官方微博</h3>
            <p className="text-xs text-foreground-500">@天狼影院官方</p>
          </div>
        </div>

        <div className="mt-10 p-6 rounded-xl bg-background-50 border border-background-200/60 max-w-xl mx-auto">
          <h3 className="text-sm font-semibold text-foreground-800 mb-4 text-center">工作时间</h3>
          <div className="space-y-2 text-center">
            <p className="text-sm text-foreground-600">周一至周五：9:00 - 18:00</p>
            <p className="text-sm text-foreground-600">周六至周日：10:00 - 17:00</p>
            <p className="text-xs text-foreground-500 mt-3">客服团队会在24小时内回复您的邮件</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Page header */}
        <section className="w-full px-4 md:px-6 py-8 md:py-12" aria-label="关于我们页头">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">关于天狼影院</h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
              高清免费在线追剧平台，海量剧集每日更新，流畅不缓冲
            </p>
          </div>
        </section>

        <BrandIntro />
        <MissionValues />
        <Testimonials />
        <ContactInfo />
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于天狼影院',
            description: '天狼影院是国内领先的在线影视平台，提供电视剧、电影、动漫、综艺、纪录片等高清免费在线观看服务。',
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://tianlangcinema.com/' },
                { '@type': 'ListItem', position: 2, name: '关于我们' },
              ],
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '天狼影院',
            url: 'https://tianlangcinema.com',
            description: '天狼影院 - 电视剧全集连播，最新剧集高清免费在线观看',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              email: 'contact@tianlangcinema.com',
              availableLanguage: 'Chinese',
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '天狼影院是什么平台？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '天狼影院是国内领先的在线影视平台，提供电视剧、电影、动漫、综艺、纪录片等高清免费在线观看服务，无需注册即可流畅追剧。',
                },
              },
              {
                '@type': 'Question',
                name: '天狼影院的优势是什么？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '天狼影院的核心优势包括：1080P高清画质、CDN加速不缓冲、每日海量更新、多端同步观看、完全免费无需注册。',
                },
              },
              {
                '@type': 'Question',
                name: '如何联系天狼影院？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '您可以通过邮箱contact@tianlangcinema.com联系我们，或关注微信公众号"天狼影院"、微博@天狼影院官方获取最新动态。',
                },
              },
            ],
          }),
        }}
      />
    </>
  );
}